/**
 * Acquire the rear camera at 240p / 60fps. Falls back gracefully if the
 * exact constraint isn't honored (Chrome may pick the closest match).
 */
export async function openRearCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 426 },
      height: { ideal: 240 },
      frameRate: { ideal: 60, max: 60 },
    },
    audio: true, // for future audio-spike contact detection
  });
}

export interface RecordingHandle {
  /** Stop the recording and resolve with the produced video Blob. */
  stop(): Promise<Blob>;
  /** Approximate FPS as actually negotiated by the browser. */
  fps(): number;
}

/** Start an MP4/WebM recording from a MediaStream. */
export function startRecording(stream: MediaStream): RecordingHandle {
  const chunks: BlobPart[] = [];
  const mimeType = pickSupportedMime();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

  recorder.addEventListener('dataavailable', (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  });
  recorder.start(/* timeslice */ 1000);

  const videoTrack = stream.getVideoTracks()[0];
  const settings = videoTrack?.getSettings();

  return {
    fps(): number {
      return settings?.frameRate ?? 60;
    },
    stop(): Promise<Blob> {
      return new Promise((resolve) => {
        recorder.addEventListener(
          'stop',
          () => {
            const blob = new Blob(chunks, { type: recorder.mimeType });
            resolve(blob);
          },
          { once: true },
        );
        recorder.stop();
      });
    },
  };
}

function pickSupportedMime(): string | undefined {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return undefined;
}
